import type React from 'react';

import { useCallback, useMemo, useState } from 'react';

export type WizardStepProps<TData> = {
  back: () => void;
  data: Partial<TData>;
  isFirst: boolean;
  isLast: boolean;
  next: () => void;
  setData: React.Dispatch<React.SetStateAction<Partial<TData>>>;
};

export type WizardStepComponent<TData> = React.ComponentType<
  WizardStepProps<TData>
>;

export type WizardProps<TData, TStep extends PropertyKey> = {
  initialData?: Partial<TData>;
  initialStep?: TStep;
  onFinish?: (data: Partial<TData>) => void;
  stepComponents: Record<TStep, WizardStepComponent<TData>>;
};

export function Wizard<TData, TStep extends PropertyKey>({
  stepComponents,
  initialData,
  initialStep,
  onFinish,
}: WizardProps<TData, TStep>) {
  const steps = useMemo(
    () => Object.keys(stepComponents) as TStep[],
    [stepComponents],
  );

  const initialIndex = useMemo(() => {
    if (!initialStep) return 0;
    const idx = steps.indexOf(initialStep);
    return idx >= 0 ? idx : 0;
  }, [initialStep, steps]);

  const [stepIndex, setStepIndex] = useState(initialIndex);
  const [data, setData] = useState<Partial<TData>>(initialData ?? {});

  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i >= steps.length - 1) {
        onFinish?.(data);
        return i;
      }
      return i + 1;
    });
  }, [data, onFinish, steps.length]);

  if (!step) return null;

  const Current: WizardStepComponent<TData> = stepComponents[step];

  return (
    <Current
      back={back}
      data={data}
      isFirst={isFirst}
      isLast={isLast}
      next={next}
      setData={setData}
    />
  );
}
